/**
 * VAT Calculator Service
 * 
 * Service for calculating VAT (Value Added Tax) and other tax-related amounts.
 * This is a JavaScript port of the Java vatCalc service.
 * 
 * @module vatCalc
 */

import { Storage } from './Storage';

/**
 * Rounds a number to specified decimal places using half-up rounding
 * @param {number} value - The value to round
 * @param {number} decimals - Number of decimal places
 * @returns {number} Rounded value
 */
function roundHalfUp(value, decimals = 5) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}

/**
 * Gets tax rate for a given tax label from the store's category data
 * @param {string} taxLabel - Tax category label (A, B, T, I, etc.)
 * @param {string} storeDB - Database name
 * @returns {Promise<number>} Tax rate as decimal (e.g., 0.16 for 16%)
 */
async function getTaxRate(taxLabel, storeDB) {
    try {
        // Get categories from storage
        const categories = await Storage.getCategories(storeDB);
        
        // Find category with matching tax label
        const category = categories?.find(cat => cat.TaxLabels === taxLabel);
        
        if (category && category.TaxRate !== undefined) {
            return Number(category.TaxRate) / 100; // Convert percentage to decimal
        }
        
        // Default rates if not found in categories
        const defaultRates = {
            'A': 0.16,  // Standard VAT 16%
            'B': 0.16,  // MTV (same as standard)
            'T': 0.02,  // Tourism levy 2%
            'I': 0.03,  // Insurance tax 3%
            'D': 0.00,  // Exempt
            'C3': 0.00  // Exempt
        };
        
        return defaultRates[taxLabel] || 0.16;
    } catch (err) {
        console.error(`Error getting tax rate for ${taxLabel}:`, err);
        return 0.16; // Default to standard VAT
    }
}

/**
 * Calculates the total VAT amount based on tax labels
 * 
 * @param {number} total - The total amount to calculate VAT on
 * @param {boolean} isInc - Whether the amount is tax-inclusive
 * @param {string} taxLabel - The tax category label (e.g., "A", "B")
 * @param {number} recommendedRetail - The recommended retail price (used for MTV calculations)
 * @param {string} storeDB - The database name
 * @returns {Promise<number>} The calculated VAT amount
 */
export async function getVatTotal(total, isInc, taxLabel, recommendedRetail, storeDB) {
    isInc = true; // Force tax-inclusive
    
    if (total === 0) {
        return 0;
    }
    
    let vat = 0;
    
    switch (taxLabel) {
        case 'A':
            vat = await getVatAmount(total, isInc, taxLabel, storeDB);
            break;
        case 'B':
            vat = await getMTVAmount(total, 1, isInc, recommendedRetail, storeDB);
            break;
        default:
            vat = 0;
    }
    
    return vat;
}

/**
 * Calculates the VAT amount for standard rated items
 * 
 * @param {number} amount - The amount to calculate VAT on
 * @param {boolean} incl - Whether the amount is tax-inclusive
 * @param {string} taxLabel - The tax category label (e.g., "A", "D", "C3")
 * @param {string} storeDB - The database name
 * @returns {Promise<number>} The calculated VAT amount
 */
export async function getVatAmount(amount, incl, taxLabel, storeDB) {
    if (amount === 0) {
        return 0;
    }
    
    incl = true; // Force tax-inclusive
    
    // Exempt categories
    if (taxLabel === 'D' || taxLabel === 'C3') {
        return 0;
    }
    
    const vatRate = await getTaxRate('A', storeDB);
    const rate = vatRate + 1; // e.g., 1.16 for 16% VAT
    
    let vatAmount;
    if (incl) {
        // Tax-inclusive: Extract VAT from total
        // VAT = Amount - (Amount / 1.16)
        vatAmount = amount - roundHalfUp(amount / rate, 5);
    } else {
        // Tax-exclusive: Add VAT to amount
        // VAT = (Amount * 0.16)
        vatAmount = roundHalfUp(amount * vatRate, 5);
    }
    
    return vatAmount;
}

/**
 * Calculates the VAT amount for minimum taxable value (MTV) items
 * 
 * @param {number} amount - The actual price to calculate VAT on
 * @param {number} qty - The quantity of items
 * @param {boolean} incl - Whether the amount is tax-inclusive
 * @param {number} mtvAmount - The minimum taxable value amount
 * @param {string} storeDB - The database name
 * @returns {Promise<number>} The calculated VAT amount based on MTV principles
 */
export async function getMTVAmount(amount, qty, incl, mtvAmount, storeDB) {
    if (amount === 0) {
        return 0;
    }
    
    incl = true; // Force tax-inclusive
    
    // Get unit price
    let unitPrice = roundHalfUp(amount / qty, 5);
    
    const taxRate = await getTaxRate('B', storeDB);
    const rate = taxRate + 1; // e.g., 1.16
    
    if (incl) {
        unitPrice = unitPrice * rate;
    }
    
    let mtvValue = mtvAmount;
    
    if (incl) {
        let vatFromActual, vatFromMTV;
        
        // Compare actual price with MTV
        if (unitPrice > mtvValue) {
            // Use actual price
            vatFromActual = unitPrice - roundHalfUp(unitPrice / rate, 5);
            return vatFromActual * qty;
        } else {
            // Use MTV
            vatFromMTV = mtvValue - roundHalfUp(mtvValue / rate, 5);
            return vatFromMTV * qty;
        }
    } else {
        // Tax-exclusive calculation
        mtvValue = roundHalfUp(mtvValue / rate, 5);
        
        if (unitPrice > mtvValue) {
            let vat = roundHalfUp(unitPrice * rate, 5) - unitPrice;
            return vat;
        } else {
            let vat = roundHalfUp(mtvValue * rate, 5) - mtvValue;
            return vat * qty;
        }
    }
}

/**
 * Calculates the tourism levy amount
 * 
 * @param {number} amount - The amount to calculate tourism levy on
 * @param {boolean} incl - Whether the amount is tax-inclusive
 * @param {string} storeDB - The database name
 * @returns {Promise<number>} The calculated tourism levy amount
 */
export async function getTourismLevyAmount(amount, incl, storeDB) {
    if (amount === 0) {
        return 0;
    }
    
    let baseAmount = amount;
    
    if (incl) {
        // Remove standard VAT first
        const vatRate = await getTaxRate('A', storeDB);
        baseAmount = roundHalfUp(amount / (vatRate + 1), 5);
    }
    
    const tourismRate = await getTaxRate('T', storeDB);
    return baseAmount * tourismRate;
}

/**
 * Calculates the insurance tax amount
 * 
 * @param {number} amount - The amount to calculate insurance tax on
 * @param {boolean} incl - Whether the amount is tax-inclusive
 * @param {string} storeDB - The database name
 * @returns {Promise<number>} The calculated insurance tax amount
 */
export async function getInsuranceAmount(amount, incl, storeDB) {
    if (amount === 0) {
        return 0;
    }
    
    let baseAmount = amount;
    
    if (incl) {
        // Add standard VAT first
        const vatRate = await getTaxRate('A', storeDB);
        baseAmount = amount * (vatRate + 1);
    }
    
    const insuranceRate = await getTaxRate('I', storeDB);
    const rate = insuranceRate + 1;
    
    return baseAmount - roundHalfUp(baseAmount / rate, 5);
}

/**
 * Calculates excl total (total excluding VAT)
 * 
 * @param {number} total - The tax-inclusive total
 * @param {number} vatAmount - The VAT amount
 * @returns {number} The tax-exclusive total
 */
export function getExclTotal(total, vatAmount) {
    return total - vatAmount;
}

export default {
    getVatTotal,
    getVatAmount,
    getMTVAmount,
    getTourismLevyAmount,
    getInsuranceAmount,
    getExclTotal
};
